import { useEffect, useRef, useState } from "react"
import { noticeStore } from "../../store/noticeStore";
import { useAtomValue } from "jotai";
import './Notice.css'
import { ReactComponent as IcPin } from "assets/svgs/icon/ic_pin_24.svg";
import { useTranslation } from "react-i18next";
import { Header } from "components/common/header/Header";
import { useQuery } from "hooks/useQuery";
import { getServiceId, getLocale } from "common/service";

interface NoticeItem {
    noticeNo: number,
    title: string,
    content: string,
    regTime: string,
    noticeType?: string,
    fixedYn?: string
}

interface NoticeItemProps {
    noticeNo: number,
    title: string,
    content: string,
    regDate: string,
    fixedYn: boolean
}

// 한 번에 불러올 공지 수. 목록 하단에 닿으면 다음 페이지를 이어붙인다.
const PAGE_SIZE = 20

export const Notice = () => {
    const { t } = useTranslation();
    const { navigate } = useQuery()
    const {noticeList, getNoticeList, getNoticeListMore} = noticeStore;
    const noticeListState = useAtomValue(noticeList.stateAtom);
    const serviceId = getServiceId()
    const locale = getLocale()
    const rawList = noticeListState?.data?.data?.list
        || noticeListState?.data?.list
        || noticeListState?.data?.data
        || noticeListState?.data
        || [];
    const dataList: NoticeItem[] = Array.isArray(rawList)
        ? rawList.map((notice: any) => ({
            noticeNo: Number(notice.noticeNo ?? notice.notice_id ?? 0),
            title: notice.title ?? notice.notice_title ?? '',
            content: notice.content ?? notice.notice_content ?? '',
            regTime: notice.regTime ?? notice.regDt ?? notice.reg_date ?? '',
            noticeType: notice.noticeType,
            fixedYn: notice.fixedYn ?? notice.fixed_yn ?? notice.isFixed,
        }))
        : [];

    const [page, setPage] = useState(1)
    const loadMoreRef = useRef<HTMLDivElement | null>(null)

    // 전체 건수는 첫 페이지 응답의 pageInfo 를 유지하므로, 누적 길이와 비교해 다음 페이지 유무를 판단한다.
    // 마지막 페이지가 가득 차지 않았으면(중복 제거 등으로 짧게 온 경우) 더 요청하지 않는다.
    // 이 조건이 없으면 빈 페이지가 돌아올 때 같은 요청을 무한 반복할 수 있다.
    const totalCount: number = noticeListState?.data?.pageInfo?.totalCount ?? 0
    const isPending = noticeListState.isPending
    const hasMore = dataList.length > 0
        && dataList.length < totalCount
        && dataList.length >= page * PAGE_SIZE

    useEffect(() => {
        setPage(1)
        getNoticeList({
            serviceId,
            locale,
            pageNo: 1,
            pageSize: PAGE_SIZE,
        });
    }, [serviceId, locale])

    // 목록 하단 감시 → 다음 페이지 이어붙이기
    useEffect(() => {
        const target = loadMoreRef.current
        if (!target || !hasMore || isPending) return

        const observer = new IntersectionObserver((entries) => {
            if (!entries[0].isIntersecting) return
            const next = page + 1
            setPage(next)
            getNoticeListMore({
                serviceId,
                locale,
                pageNo: next,
                pageSize: PAGE_SIZE,
            })
        }, { threshold: 0.1 })

        observer.observe(target)
        return () => observer.disconnect()
    }, [hasMore, isPending, page, serviceId, locale])

    const NoticeItem = ({
        noticeNo,
        title,
        content,
        regDate,
        fixedYn
    }: NoticeItemProps) => {
        return (
            <button className="notice-item-container" onClick={() => navigate(`/notice/detail?noticeNo=${noticeNo}`)}>
                <span className="notice-title">{fixedYn && <IcPin className="notice-item-pin" />} {title}</span>
                <span className="notice-content">{content}</span>
                <span className="notice-reg-date">{regDate}</span>
            </button>
        )
    }


    return (
        <div className="notice-wrapper">
            <Header title={t('notice.title')} isBack />
            <div className="notice-item-list">
                {isPending && dataList.length === 0 && (
                    <div className="notice-status">{t('notice.loading')}</div>
                )}

                {noticeListState.isError && (
                    <div className="notice-status">{t('notice.error')}</div>
                )}

                {noticeListState.isReady && dataList.length === 0 && (
                    <div className="notice-status">{t('notice.empty')}</div>
                )}

                {dataList.map((notice) => {
                    return (
                        <NoticeItem
                            key={notice.noticeNo}
                            noticeNo={notice.noticeNo}
                            title={notice.title}
                            content={notice.content}
                            regDate={notice.regTime}
                            fixedYn={notice.fixedYn === 'Y' || notice.noticeType === 'I'}
                        />
                    )
                })

                }

                {/* 화면에 들어오면 다음 페이지를 불러오는 감시 영역 */}
                {hasMore && (
                    <div ref={loadMoreRef} className="notice-status">
                        {isPending ? t('notice.loading') : ''}
                    </div>
                )}
            </div>
        </div>
    )
}
